import PropTypes from "prop-types";
import {
  createContext,
  useEffect,
  useReducer,
  useCallback,
  useMemo,
} from "react";
// utils
import axios from "../utils/axios";
import localStorageAvailable from "../utils/localStorageAvailable";
import { useNavigate } from "react-router-dom";
//
import { isValidToken, setSession, setRefresh, setLevel } from "./utils";
import { setDateTimeFormats } from "../utils/formatTime";
import { setNumberFormatContext } from "../utils/formatNumber";
const backendUrl = process.env.REACT_APP_BACKEND_URL;

const ownerPermissions = {
  canViewDashboard: true,
  canViewStaff: true,
  canAddStaffAndRoles: true,
  canEditStaffDetails: true,
  canEditPermission: true,
  canViewVenueCustomers: true,
  canAccessReports: true,
  canChangeSystemSettings: true,
  canExportCustomer: true,
  canLogInToMultipleDevices: true,
};

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
  paymentMethods: null,
};

const reducer = (state, action) => {
  if (action.type === "INITIAL") {
    return {
      isInitialized: true,
      isAuthenticated: action?.payload?.isAuthenticated,
      user: action?.payload?.user,
      paymentMethods: action?.payload?.paymentMethods?.results,
    };
  }
  if (action.type === "LOGIN") {
    return {
      ...state,
      isInitialized: true,
      isAuthenticated: true,
      user: action?.payload?.user,
      paymentMethods: action?.payload?.paymentMethods?.results,
    };
  }
  if (action.type === "REGISTER") {
    return {
      ...state,
      isAuthenticated: true,
      user: action?.payload?.user,
      paymentMethods: action?.payload?.paymentMethods?.results,
    };
  }

  if (action.type === "RESET") {
    return {
      ...state,
      isAuthenticated: true,
      user: action?.payload?.user,
    };
  }

  if (action.type === "LOGOUT") {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
      paymentMethods: null,
    };
  }
  if (action.type === "UPDATE_USER") {
    return {
      ...state,
      user: action?.payload?.user,
    };
  }
  if (action.type === "UPDATE_PAYMENT_METHOD") {
    return {
      ...state,
      paymentMethods: action?.payload?.paymentMethods,
    };
  }
  if (action.type === "UPDATE_FRANCHISE") {
    return {
      ...state,
      user: {
        ...state.user,
        franchiseId: action.payload.franchiseId,
      },
    };
  }
  if (action.type === "DELETE_PAYMENT_METHOD") {
    return {
      ...state,
      paymentMethods: action?.payload?.paymentMethods,
    };
  }

  return state;
};

export const AuthContext = createContext(null);

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const navigate = useNavigate();
  const storageAvailable = localStorageAvailable();

  useEffect(() => {
    setDateTimeFormats(state.user?.franchiseId);
    setNumberFormatContext(state.user?.franchiseId);
  }, [state.user?.franchiseId]);

  const initialize = useCallback(async () => {
    try {
      const refreshToken = storageAvailable
        ? localStorage.getItem("refreshToken")
        : "";
      const accessToken = storageAvailable
        ? localStorage.getItem("accessToken")
        : "";

      if (refreshToken && isValidToken(accessToken)) {
        setSession(accessToken);
        setRefresh(refreshToken);

        const response = await axios.post(`${backendUrl}/authf/get-profile`, {
          refreshToken,
        });

        const {
          user,
          userType,
          roleType,
          paymentMethods,
          isOverdue,
          franchiseId: profileFranchise,
        } = response.data;
        const isOwnerProfile =
          [userType, roleType, user?.userType, user?.role]
            .filter(Boolean)
            .some((type) => type.toLowerCase() === "owner") ||
          Boolean(profileFranchise && !user?.franchiseId) ||
          Boolean(localStorage.getItem("pendingOwnerId"));

        if (isOwnerProfile) {
          const storedFranchise = localStorage.getItem("ownerFranchise");
          if (!profileFranchise && !storedFranchise) {
            const ownerId = user?.id || user?._id;
            if (ownerId) localStorage.setItem("pendingOwnerId", ownerId);
            user.role = "owner";
            user.userType = "owner";
            user.isSuperAdmin = true;
            user.permissions = user.permissions || ownerPermissions;
            user.isProfileComplete = false;
            dispatch({
              type: "INITIAL",
              payload: {
                isAuthenticated: true,
                user,
                paymentMethods,
              },
            });
            return;
          }

          const storedFranchiseData = storedFranchise
            ? JSON.parse(storedFranchise)
            : {};
          const profileFranchiseData =
            profileFranchise?.franchiseId || profileFranchise || {};
          const franchiseId = {
            ...storedFranchiseData,
            ...profileFranchiseData,
            id:
              profileFranchiseData?.id ||
              profileFranchiseData?._id ||
              storedFranchiseData?.id ||
              storedFranchiseData?._id,
          };
          localStorage.setItem("ownerFranchise", JSON.stringify(franchiseId));
          user.role = "owner";
          user.userType = "owner";
          user.isSuperAdmin = true;
          user.permissions = ownerPermissions;
          user.franchiseId = franchiseId;
          user.isProfileComplete = true;
        }
        user.isOverdue =
          isOverdue ?? user?.isOverdue ?? user?.franchiseId?.isOverdue;

        if (user?.franchiseId?.levels?.length) {
          if (!localStorage.getItem("levelSelected")) {
            setLevel(user?.franchiseId?.levels[0]);
          }
        }

        user.displayName = `${user?.firstName} ${user?.lastName} `;
        if (user?.franchiseId?.status === "inActive") {
          dispatch({
            type: "INITIAL",
            payload: {
              isAuthenticated: false,
              user: null,
              paymentMethods: null,
            },
          });
        } else {
          dispatch({
            type: "INITIAL",
            payload: {
              isAuthenticated: true,
              user,
              paymentMethods,
            },
          });
        }
      } else {
        if (accessToken || refreshToken) {
          setSession(null);
          setRefresh(null);
          localStorage.removeItem("ownerFranchise");
          localStorage.removeItem("pendingOwnerId");
        }
        dispatch({
          type: "INITIAL",
          payload: {
            isAuthenticated: false,
            user: null,
            paymentMethods: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      setSession(null);
      setRefresh(null);
      localStorage.removeItem("ownerFranchise");
      localStorage.removeItem("pendingOwnerId");
      dispatch({
        type: "INITIAL",
        payload: {
          isAuthenticated: false,
          user: null,
          paymentMethods: null,
        },
      });
    }
  }, [storageAvailable]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const selectOwnerFranchise = useCallback(
    async (owner, franchise, browserInfo) => {
      const requestedFranchise = franchise?.franchiseId || franchise;
      const franchiseId = requestedFranchise?.id || requestedFranchise?._id;
      const response = await axios.post(
        `${backendUrl}/authf/select-franchise`,
        {
          franchiseId,
          browserInfo,
        }
      );
      const selectedUser = response.data?.user || owner;
      const selectedFranchiseData =
        response.data?.franchiseId ||
        selectedUser?.franchiseId ||
        requestedFranchise;
      const selectedFranchise = {
        ...selectedFranchiseData,
        id: selectedFranchiseData?.id || selectedFranchiseData?._id,
      };
      const tokens = response.data?.tokens;

      if (tokens?.access?.token) setSession(tokens.access.token);
      if (tokens?.refresh?.token) setRefresh(tokens.refresh.token);

      const ownerUser = {
        ...selectedUser,
        role: "owner",
        userType: "owner",
        isSuperAdmin: true,
        permissions: selectedUser?.permissions || ownerPermissions,
        franchiseId: selectedFranchise,
        displayName: `${selectedUser?.firstName || ""} ${
          selectedUser?.lastName || ""
        }`.trim(),
        isOverdue: selectedFranchise?.isOverdue || false,
      };

      if (selectedFranchise?.levels?.length) {
        setLevel(selectedFranchise.levels[0]);
      }
      localStorage.setItem("ownerFranchise", JSON.stringify(selectedFranchise));

      dispatch({
        type: "LOGIN",
        payload: {
          user: ownerUser,
          paymentMethods: response.data?.paymentMethods,
        },
      });
      navigate("/dashboard/app");
      return ownerUser;
    },
    [navigate]
  );

  // LOGIN
  const login = useCallback(
    async (userName, password, ipAddress, browserInfo) => {
      const response = await axios.post(`${backendUrl}/authf/login`, {
        userName,
        password,
        ipAddress,
        browserInfo,
      });
      const { tokens, user, userType, roleType, paymentMethods, isOverdue } =
        response.data;

      user.displayName = `${user?.firstName} ${user?.lastName}`;

      setSession(tokens?.access?.token);
      setRefresh(tokens?.refresh?.token);

      const isOwnerLogin = [userType, roleType, user?.userType, user?.role]
        .filter(Boolean)
        .some((type) => type.toLowerCase() === "owner");

      if (isOwnerLogin) {
        if (user?.franchiseId) {
          const completedOwner = {
            ...user,
            role: "owner",
            userType: "owner",
            isSuperAdmin: true,
            permissions: user.permissions || ownerPermissions,
            isProfileComplete: true,
            isOverdue:
              isOverdue ?? user?.isOverdue ?? user?.franchiseId?.isOverdue,
          };

          localStorage.removeItem("pendingOwnerId");
          localStorage.setItem(
            "ownerFranchise",
            JSON.stringify(user.franchiseId)
          );
          if (user.franchiseId?.levels?.length) {
            setLevel(user.franchiseId.levels[0]);
          }
          dispatch({
            type: "LOGIN",
            payload: { user: completedOwner, paymentMethods },
          });
          navigate("/dashboard/app", { replace: true });
          return completedOwner;
        }

        const franchiseResponse = await axios.get(`${backendUrl}/franchise`, {
          params: { OwnerId: user.id || user._id, page: 1, limit: 1000 },
        });
        const franchiseData = franchiseResponse.data;
        const franchises = Array.isArray(franchiseData)
          ? franchiseData
          : franchiseData?.results ||
            franchiseData?.franchises ||
            franchiseData?.data ||
            [];

        if (franchises.length === 0) {
          const ownerId = user.id || user._id;
          const incompleteOwner = {
            ...user,
            role: "owner",
            userType: "owner",
            isSuperAdmin: true,
            permissions: user.permissions || ownerPermissions,
            isProfileComplete: false,
          };

          if (ownerId) localStorage.setItem("pendingOwnerId", ownerId);
          localStorage.removeItem("ownerFranchise");
          dispatch({
            type: "LOGIN",
            payload: {
              user: incompleteOwner,
              paymentMethods,
            },
          });
          navigate("/auth/sign-up", { replace: true });
          return incompleteOwner;
        }

        if (franchises.length === 1) {
          return selectOwnerFranchise(user, franchises[0], browserInfo);
        }

        return { requiresFranchiseSelection: true, owner: user, franchises };
      }

      localStorage.removeItem("ownerFranchise");
      localStorage.removeItem("pendingOwnerId");
      user.isOverdue =
        isOverdue ?? user?.isOverdue ?? user?.franchiseId?.isOverdue;
      dispatch({
        type: "LOGIN",
        payload: {
          user,
          paymentMethods,
        },
      });
      if (user.permissions?.canViewDashboard) {
        navigate("/dashboard/app");
      } else if (user.permissions?.canViewStaff) {
        navigate("/dashboard/user/account");
      } else if (user.permissions?.canAccessReports) {
        navigate("/dashboard/report");
      } else if (user?.permissions?.canViewVenueCustomers) {
        navigate("/dashboard/customer/list");
      } else {
        navigate("/dashboard/settings/new");
      }
      return user;
    },
    [navigate, selectOwnerFranchise]
  );

  const completeOtpVerification = useCallback(async ({ email, otp }) => {
    const response = await axios.post(`${backendUrl}/owner/verify-otp`, {
      email,
      otp,
    });
    const verifiedOwner = response.data?.user || response.data?.owner;
    const tokens = response.data?.tokens;

    if (!verifiedOwner) {
      throw new Error("OTP verification did not return an owner.");
    }

    setSession(tokens?.access?.token);
    setRefresh(tokens?.refresh?.token);

    const owner = {
      ...verifiedOwner,
      role: verifiedOwner.role || "owner",
      userType: verifiedOwner.userType || "owner",
      isSuperAdmin: true,
      permissions: verifiedOwner.permissions || ownerPermissions,
      isProfileComplete: Boolean(verifiedOwner.franchiseId),
      displayName: `${verifiedOwner?.firstName || ""} ${
        verifiedOwner?.lastName || ""
      }`.trim(),
    };
    const ownerId = owner.id || owner._id;

    if (ownerId) localStorage.setItem("pendingOwnerId", ownerId);
    if (owner.isProfileComplete) {
      localStorage.removeItem("pendingOwnerId");
      localStorage.setItem(
        "ownerFranchise",
        JSON.stringify(owner.franchiseId)
      );
    } else {
      localStorage.removeItem("ownerFranchise");
    }
    localStorage.removeItem("pendingRegistration");

    dispatch({
      type: "LOGIN",
      payload: { user: owner, paymentMethods: response.data?.paymentMethods },
    });

    return owner;
  }, []);

  // REGISTER
  const register = useCallback(async (email, password, firstName, lastName) => {
    const response = await axios.post(
      "https://api-dev-minimal-v4.vercel.app/api/account/register",
      {
        email,
        password,
        firstName,
        lastName,
      }
    );
    const { accessToken, user } = response.data;

    localStorage.setItem("accessToken", accessToken);

    dispatch({
      type: "REGISTER",
      payload: {
        user,
      },
    });
  }, []);

  // LOGOUT
  const logout = useCallback(() => {
    setSession(null);
    setRefresh(null);
    localStorage.removeItem("ownerFranchise");
    localStorage.removeItem("pendingOwnerId");
    dispatch({
      type: "LOGOUT",
    });
  }, []);

  // Reset
  const resetPassword = useCallback(
    async (old_password, new_password, user) => {
      const response = await axios.patch(
        `${backendUrl}/franchiseUser/changePassword/${user?.id}`,
        {
          old_password,
          new_password,
        }
      );

      dispatch({
        type: "RESET",
        payload: {
          user,
        },
      });
      return response;
    },
    []
  );

  const updateUser = useCallback((updatedUser) => {
    updatedUser.displayName = `${updatedUser?.firstName} ${updatedUser?.lastName}`;
    dispatch({
      type: "UPDATE_USER",
      payload: {
        user: updatedUser,
      },
    });
  }, []);

  const addPaymentMethod = useCallback(
    (newPaymentMethod) => {
      const updatedPaymentMethods = [...state.paymentMethods, newPaymentMethod];
      dispatch({
        type: "UPDATE_PAYMENT_METHOD",
        payload: {
          paymentMethods: updatedPaymentMethods,
        },
      });
    },
    [state.paymentMethods]
  );

  const updatePaymentMethod = useCallback(
    (updatedPaymentMethod) => {
      dispatch({
        type: "UPDATE_PAYMENT_METHOD",
        payload: {
          paymentMethods: state.paymentMethods.map((method) =>
            method.id === updatedPaymentMethod.id
              ? updatedPaymentMethod
              : method
          ),
        },
      });
    },
    [state.paymentMethods]
  );
  const deletePaymentMethod = useCallback(
    (paymentMethodId) => {
      const updatedPaymentMethods = state.paymentMethods.filter(
        (method) => method.id !== paymentMethodId
      );
      dispatch({
        type: "UPDATE_PAYMENT_METHOD",
        payload: {
          paymentMethods: updatedPaymentMethods,
        },
      });
    },
    [state.paymentMethods]
  );

  const updateFranchise = useCallback((updateFranchise) => {
    const normalizedFranchise = {
      ...updateFranchise,
      id: updateFranchise?.id || updateFranchise?._id,
    };

    if (state.user?.userType === "owner" || state.user?.role === "owner") {
      localStorage.setItem(
        "ownerFranchise",
        JSON.stringify(normalizedFranchise)
      );
    }

    dispatch({
      type: "UPDATE_FRANCHISE",
      payload: {
        franchiseId: normalizedFranchise,
      },
    });
  }, [state.user?.role, state.user?.userType]);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      availablePaymentOptions: state.paymentMethods,
      method: "jwt",
      login,
      completeOtpVerification,
      selectOwnerFranchise,
      register,
      logout,
      resetPassword,
      updateUser,
      addPaymentMethod,
      updatePaymentMethod,
      deletePaymentMethod,
      updateFranchise,
    }),
    [
      state.isAuthenticated,
      state.isInitialized,
      state.user,
      state.paymentMethods,
      login,
      completeOtpVerification,
      selectOwnerFranchise,
      logout,
      register,
      resetPassword,
      updateUser,
      addPaymentMethod,
      updatePaymentMethod,
      deletePaymentMethod,
      updateFranchise,
    ]
  );

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  );
}
